import type { StacItem } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import { hasTileArchive, tileSourceOf } from 'src/shared/catalogue/tileSource';

/**
 * Why nothing is drawn on the map, when nothing is.
 *
 * A version with data and no archive is perfectly valid and simply cannot be drawn; an archive
 * that declares no layers cannot be drawn either, and nothing else can supply the name. Both
 * leave a map showing only a footprint, and a map that says nothing leaves the reader to guess
 * whether it is broken.
 */
export type TileDrawingIssue = 'no-archive' | 'no-layers' | null;

export const tileDrawingIssueFor = (
    items: StacItem[],
    /** What the engine read out of each archive, keyed by version id. */
    servedLayers: Record<string, string[]>
): TileDrawingIssue => {
    // The map already says nothing is in force. A second message adds nothing.
    if (items.length === 0) {
        return null;
    }

    if (items.some(item => tileSourceOf(item, servedLayers[item.id]) !== null)) {
        return null;
    }

    const archives = items.filter(hasTileArchive);
    if (archives.length === 0) {
        return 'no-archive';
    }

    // Before the engine has answered there is nothing to report: warning then would flash a
    // complaint on every load that resolves a moment later.
    return archives.some(item => servedLayers[item.id]?.length === 0) ? 'no-layers' : null;
};
